import { useEffect, useState } from "react";
import { Printer, CheckCircle, Zap, Send, Trash2, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { PageHeader } from "../components/ui/PageHeader";
import { Select } from "../components/ui/Select";
import { DataTable, type Column } from "../components/ui/DataTable";
import { RowActions } from "../components/ui/RowActions";
import { PreviewModal } from "../components/PreviewModal";
import { eur, fmtDate } from "../lib/format";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

interface PlatformInvoice {
  id: string;
  number: string;
  client?: { id: string; name: string };
  packageName?: string | null;
  packageKey?: string | null;
  periodYear: number;
  periodMonth: number;
  issueDate: string;
  dueDate: string;
  status: "TE_BETALEN" | "BETAALD";
  monthlyPriceExcl: string | number;
  discountAmount: string | number;
  discountPercentage: string | number | null;
  discountLabel?: string | null;
  subtotalExcl: string | number;
  vatAmount: string | number;
  totalIncl: string | number;
  paidAt?: string | null;
  emailedAt?: string | null;
}

const MONTHS_NL = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

function periodLabel(pi: PlatformInvoice): string {
  const m = MONTHS_NL[(pi.periodMonth - 1 + 12) % 12] ?? String(pi.periodMonth);
  return `${m} ${pi.periodYear}`;
}

export default function PayableInvoices() {
  const { activeCompany } = useAuth();
  const isPlatform = !!activeCompany?.isPlatform;
  const [items, setItems] = useState<PlatformInvoice[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/api/platform-invoices", { params: statusFilter ? { status: statusFilter } : {} })
      .then((res) => setItems(res.data))
      .finally(() => setLoading(false));
  };

  // Bij openen: zorg dat de huidige maand er is (on-demand), dan laad de lijst.
  useEffect(() => {
    api
      .post("/api/platform-invoices/ensure-current")
      .catch(() => {})
      .finally(() => load());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const handleResend = async (pi: PlatformInvoice) => {
    if (!confirm(`Herinnering versturen?\n\nFactuur ${pi.number} wordt opnieuw per e-mail naar ${pi.client?.name || "de klant"} gestuurd.`)) return;
    try {
      const { data } = await api.post(`/api/platform-invoices/${pi.id}/resend`);
      flash(data.emailed ? `Herinnering verstuurd naar ${pi.client?.name || "klant"}.` : "Herinnering kon niet verstuurd worden (geen e-mailadres of SMTP niet geconfigureerd).");
      load();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Herinnering versturen mislukt.");
    }
  };

  const handleDelete = async (pi: PlatformInvoice) => {
    if (!confirm(`Factuur ${pi.number} (${pi.client?.name || "klant"}) verwijderen?\n\nDit verwijdert de factuur, de AICORE-verkoopfactuur en eventuele gekoppelde betalingen en klant-uitgave. Deze actie kan niet ongedaan worden gemaakt.`)) return;
    try {
      await api.delete(`/api/platform-invoices/${pi.id}`);
      flash("Factuur verwijderd.");
      load();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Verwijderen mislukt.");
    }
  };

  const handleViewPdf = async (pi: PlatformInvoice) => {
    try {
      const res = await api.get(`/api/platform-invoices/${pi.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      alert("PDF laden mislukt.");
    }
  };

  const handleDownloadPdf = async (pi: PlatformInvoice) => {
    try {
      const res = await api.get(`/api/platform-invoices/${pi.id}/pdf`, {
        responseType: "blob",
        params: { download: "1" },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pi.number || "factuur"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      alert("PDF downloaden mislukt.");
    }
  };

  const handleMarkPaid = async (pi: PlatformInvoice) => {
    if (!confirm(`Factuur ${pi.number} markeren als betaald?\n\nDit boekt het bedrag als inkomsten bij AICORE en — als je de uitgaven-module hebt — als uitgave in je eigen boeken.`)) return;
    try {
      await api.post(`/api/platform-invoices/${pi.id}/mark-paid`);
      flash("Factuur gemarkeerd als betaald.");
      load();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Markeren als betaald mislukt.");
    }
  };

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/superadmin/platform-invoices/generate", {});
      const prev = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
      const m = MONTHS_NL[prev.getMonth()];
      flash(`${data.created} factuur(en) aangemaakt voor ${m} ${prev.getFullYear()} (${data.skipped} overgeslagen${data.emailed ? `, ${data.emailed} gemaild` : ""}).`);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Genereren mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<PlatformInvoice>[] = [
    { key: "period", header: "Periode", accessor: (pi) => periodLabel(pi),
      render: (pi) => <span className="font-semibold text-slate-800">{periodLabel(pi)}</span> },
    ...(isPlatform
      ? [{ key: "client", header: "Klant", accessor: (pi: PlatformInvoice) => pi.client?.name || "—" } as Column<PlatformInvoice>]
      : []),
    { key: "number", header: "Nummer", accessor: (pi) => pi.number || "—" },
    { key: "package", header: "Pakket", accessor: (pi) => pi.packageName || "—" },
    { key: "price", header: "Ex BTW", accessor: (pi) => eur(pi.monthlyPriceExcl), className: "text-right",
      sortValue: (pi) => Number(pi.monthlyPriceExcl) },
    { key: "discount", header: "Korting", accessor: (pi) => {
        const amt = Number(pi.discountAmount);
        if (!amt) return "—";
        const pct = pi.discountPercentage != null ? ` (${Number(pi.discountPercentage)}%)` : "";
        return `−${eur(amt)}${pct}`;
      }, className: "text-right", sortValue: (pi) => Number(pi.discountAmount),
      render: (pi) => {
        const amt = Number(pi.discountAmount);
        if (!amt) return <span className="text-slate-400">—</span>;
        const pct = pi.discountPercentage != null ? ` (${Number(pi.discountPercentage)}%)` : "";
        return <span className="text-rose-600 font-medium">−{eur(amt)}{pct}</span>;
      } },
    { key: "vat", header: "BTW", accessor: (pi) => eur(pi.vatAmount), className: "text-right",
      sortValue: (pi) => Number(pi.vatAmount) },
    { key: "total", header: "Totaal incl.", accessor: (pi) => eur(pi.totalIncl), className: "text-right",
      sortValue: (pi) => Number(pi.totalIncl),
      render: (pi) => <span className="font-bold text-slate-900">{eur(pi.totalIncl)}</span> },
    { key: "due", header: "Verval", accessor: (pi) => fmtDate(pi.dueDate),
      sortValue: (pi) => new Date(pi.dueDate).getTime() },
    { key: "status", header: "Status", accessor: (pi) => (pi.status === "BETAALD" ? "Betaald" : "Te betalen"),
      render: (pi) =>
        pi.status === "BETAALD" ? (
          <Badge status="PAID">Betaald{pi.paidAt ? ` op ${fmtDate(pi.paidAt)}` : ""}</Badge>
        ) : (
          <Badge status="OVERDUE" pulse>Te betalen</Badge>
        ) },
    { key: "actions", header: "Acties", accessor: () => "", sortable: false, width: "1%",
      render: (pi) => (
        <RowActions
          items={[
            { label: "PDF openen", icon: FileText, onClick: () => handleViewPdf(pi) },
            { label: "Download PDF", icon: Download, onClick: () => handleDownloadPdf(pi) },
            { label: "Export PDF / printen", icon: Printer, onClick: () => setPreviewId(pi.id) },
            { label: "Markeer als betaald", icon: CheckCircle, onClick: () => handleMarkPaid(pi), hidden: pi.status === "BETAALD" },
            { label: "Herinnering versturen", icon: Send, onClick: () => handleResend(pi), hidden: !isPlatform },
            { label: "Verwijderen", icon: Trash2, onClick: () => handleDelete(pi), hidden: !isPlatform },
          ]}
        />
      ) },
  ];

  const statusOptions = [
    { value: "", label: "Alle statussen" },
    { value: "TE_BETALEN", label: "Te betalen" },
    { value: "BETAALD", label: "Betaald" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={isPlatform ? "Te betalen facturen (alle klanten)" : "Te betalen facturen"}
        subtitle={
          isPlatform
            ? "Maandelijkse abonnementsfacturen van AICORE aan alle AIBoek-klanten"
            : "Maandelijkse abonnementsfacturen van AICORE voor jouw gebruik van AIBoek"
        }
        action={
          isPlatform ? (
            <Button onClick={handleGenerate} loading={busy}>
              <Zap size={20} /> Genereer (vorige maand)
            </Button>
          ) : undefined
        }
      />

      {toast && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium px-4 py-3">
          {toast}
        </div>
      )}

      <DataTable
        rows={items}
        columns={columns}
        rowKey={(pi) => pi.id}
        searchPlaceholder="Zoek facturen..."
        storageKey="payable-invoices"
        initialSort={{ key: "period", dir: "desc" }}
        groupBy={{ getDate: (pi) => pi.issueDate, getAmount: (pi) => pi.totalIncl }}
        emptyMessage="Nog geen abonnementsfacturen."
        toolbar={
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-48"
          />
        }
      />

      <PreviewModal
        isOpen={!!previewId}
        onClose={() => setPreviewId(null)}
        kind="platform-invoices"
        id={previewId}
        title="Te betalen factuur weergave"
      />
    </div>
  );
}